import { create } from 'zustand';

const initialState = {
  // Store jobs data per chatbot ID
  jobsData: {}, // { [chatbotId]: { results: [], lastUpdated: timestamp } }
  pollingIntervals: {}, // { [chatbotId]: intervalId }
};

export const useJobsStore = create((set, get) => ({
  ...initialState,

  // Set jobs data for a specific chatbot
  setJobsData: (chatbotId, data) => {
    if (!chatbotId) {
      console.warn('setJobsData: chatbotId is required');
      return;
    }
    
    set((state) => ({
      jobsData: {
        ...state.jobsData,
        [chatbotId]: {
          results: data?.results || [],
          lastUpdated: Date.now(),
        },
      },
    }));
  },

  // Get jobs data for a specific chatbot
  getJobsData: (chatbotId) => {
    if (!chatbotId) {
      console.warn('getJobsData: chatbotId is required');
      return null;
    }
    
    const state = get();
    return state.jobsData[chatbotId] || null;
  },

  // Get all jobs data
  getAllJobsData: () => {
    const state = get();
    return state.jobsData;
  },

  // Set polling interval for a specific chatbot
  setPollingInterval: (chatbotId, intervalId) => {
    if (!chatbotId || !intervalId) {
      console.warn('setPollingInterval: chatbotId and intervalId are required');
      return;
    }
    
    set((state) => ({
      pollingIntervals: {
        ...state.pollingIntervals,
        [chatbotId]: intervalId,
      },
    }));
  },

  // Clear polling interval for a specific chatbot
  clearPollingInterval: (chatbotId) => {
    if (!chatbotId) {
      console.warn('clearPollingInterval: chatbotId is required');
      return;
    }
    
    const state = get();
    const intervalId = state.pollingIntervals[chatbotId];
    if (intervalId) {
      clearInterval(intervalId);
    }
    
    set((state) => {
      const newPollingIntervals = { ...state.pollingIntervals };
      delete newPollingIntervals[chatbotId];
      return { pollingIntervals: newPollingIntervals };
    });
  },

  // Clear all polling intervals
  clearAllPollingIntervals: () => {
    const state = get();
    Object.values(state.pollingIntervals).forEach(intervalId => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });
    
    set({ pollingIntervals: {} });
  },

  // Remove jobs data for a specific chatbot
  removeJobsData: (chatbotId) => {
    if (!chatbotId) {
      console.warn('removeJobsData: chatbotId is required');
      return;
    }
    
    set((state) => {
      const newJobsData = { ...state.jobsData };
      delete newJobsData[chatbotId];
      return { jobsData: newJobsData };
    });
  },

  // Clear all jobs data
  clearAllJobsData: () => {
    set({ jobsData: {} });
  },

  // Reset the entire store
  reset: () => {
    const state = get();
    state.clearAllPollingIntervals();
    set({ ...initialState });
  },

  // Cleanup function to be called when component unmounts
  cleanup: (chatbotId) => {
    if (chatbotId) {
      const state = get();
      state.clearPollingInterval(chatbotId);
      state.removeJobsData(chatbotId);
    }
  },
}));
